/**
 * Modal de ajuda para configuração de condições de erro do Webhook
 * Explica cada tipo de condição com exemplos práticos
 * 
 * RELEASE: 1.0.0 - 2024-12-25 - Criação inicial
 */

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import {
  BookOpen,
  AlertCircle,
  Code,
  Lightbulb,
  CheckCircle,
  XCircle,
  ArrowRight,
} from 'lucide-react';

interface WebhookErrorHelpDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Modal com explicação detalhada sobre condições de erro do webhook
 */
export function WebhookErrorHelpDialog({ open, onOpenChange }: WebhookErrorHelpDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] p-0 gap-0 overflow-hidden flex flex-col">
        <DialogHeader className="px-6 pt-6 pb-4 border-b shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-primary" />
            Ajuda: Condições de Erro do Webhook
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="flex-1 max-h-[calc(85vh-140px)]">
          <div className="px-6 space-y-6 py-4">
            {/* Introdução */}
            <div className="bg-primary/5 rounded-lg p-4 border border-primary/20">
              <h3 className="font-semibold text-sm flex items-center gap-2 mb-2">
                <AlertCircle className="h-4 w-4 text-primary" />
                O que é isso?
              </h3>
              <p className="text-sm text-muted-foreground">
                O tratamento de erro permite detectar quando uma API retorna dados vazios ou erro, 
                e pedir ao usuário para digitar novamente a informação (ex: CPF, placa, código).
              </p>
            </div>

            <Separator />

            {/* Tipo 1: Array/campo vazio */}
            <div className="space-y-3">
              <h3 className="font-semibold flex items-center gap-2">
                <Badge variant="outline" className="font-mono">1</Badge>
                Array/campo vazio
              </h3>
              
              <p className="text-sm text-muted-foreground">
                Detecta quando a resposta da API é um array vazio <code className="bg-muted px-1 rounded">[]</code> ou 
                campo nulo/vazio. Útil quando a API retorna lista vazia para dados não encontrados.
              </p>

              <div className="bg-muted/50 rounded-lg p-3 space-y-3">
                <p className="text-sm font-medium">Campo "Caminho":</p>
                <ul className="text-sm space-y-2 text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <ArrowRight className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
                    <span>
                      <strong>Deixe VAZIO</strong> para verificar a resposta inteira na raiz
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <ArrowRight className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
                    <span>
                      Use <code className="bg-muted px-1 rounded">data</code> para verificar{' '}
                      <code className="bg-muted px-1 rounded">{'{ "data": [] }'}</code>
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <ArrowRight className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
                    <span>
                      Use <code className="bg-muted px-1 rounded">resultado.items</code> para verificar{' '}
                      <code className="bg-muted px-1 rounded">{'{ "resultado": { "items": [] } }'}</code>
                    </span>
                  </li>
                </ul>
              </div>

              {/* Exemplo visual */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3">
                  <p className="text-xs font-medium text-green-700 dark:text-green-400 flex items-center gap-1 mb-2">
                    <CheckCircle className="h-3 w-3" />
                    Resposta com dados (SUCESSO)
                  </p>
                  <pre className="text-xs font-mono text-muted-foreground overflow-x-auto">
{`[
  { "id": 1, "valor": 150.00 },
  { "id": 2, "valor": 89.90 }
]`}
                  </pre>
                </div>
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                  <p className="text-xs font-medium text-red-700 dark:text-red-400 flex items-center gap-1 mb-2">
                    <XCircle className="h-3 w-3" />
                    Resposta vazia (ERRO)
                  </p>
                  <pre className="text-xs font-mono text-muted-foreground">
{`[]`}
                  </pre>
                </div>
              </div>
            </div>

            <Separator />

            {/* Tipo 2: Campo igual a valor */}
            <div className="space-y-3">
              <h3 className="font-semibold flex items-center gap-2">
                <Badge variant="outline" className="font-mono">2</Badge>
                Campo igual a valor
              </h3>
              
              <p className="text-sm text-muted-foreground">
                Detecta quando um campo específico da resposta tem um valor indicando erro.
                Útil para APIs que retornam um campo de status ou erro.
              </p>

              <div className="bg-muted/50 rounded-lg p-3 space-y-3">
                <p className="text-sm font-medium">Configuração:</p>
                <ul className="text-sm space-y-2 text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <Code className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
                    <span>
                      <strong>Caminho:</strong> onde está o campo no JSON (ex: <code className="bg-muted px-1 rounded">error</code>, <code className="bg-muted px-1 rounded">status</code>)
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Code className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
                    <span>
                      <strong>Valor:</strong> valor que indica erro (ex: <code className="bg-muted px-1 rounded">true</code>, <code className="bg-muted px-1 rounded">not_found</code>)
                    </span>
                  </li>
                </ul>
              </div>

              {/* Exemplo visual */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3">
                  <p className="text-xs font-medium text-green-700 dark:text-green-400 flex items-center gap-1 mb-2">
                    <CheckCircle className="h-3 w-3" />
                    API retorna sucesso
                  </p>
                  <pre className="text-xs font-mono text-muted-foreground overflow-x-auto">
{`{
  "error": false,
  "data": { "nome": "João" }
}`}
                  </pre>
                </div>
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                  <p className="text-xs font-medium text-red-700 dark:text-red-400 flex items-center gap-1 mb-2">
                    <XCircle className="h-3 w-3" />
                    API retorna erro
                  </p>
                  <pre className="text-xs font-mono text-muted-foreground overflow-x-auto">
{`{
  "error": true,
  "message": "Não encontrado"
}`}
                  </pre>
                  <p className="text-xs text-muted-foreground mt-2">
                    Config: Caminho=<code className="bg-muted px-0.5 rounded">error</code> / Valor=<code className="bg-muted px-0.5 rounded">true</code>
                  </p>
                </div>
              </div>
            </div>

            <Separator />

            {/* Tipo 3: Status HTTP */}
            <div className="space-y-3">
              <h3 className="font-semibold flex items-center gap-2">
                <Badge variant="outline" className="font-mono">3</Badge>
                Status HTTP 4xx/5xx
              </h3>
              
              <p className="text-sm text-muted-foreground">
                Detecta erros baseados no código de status HTTP da resposta (400, 404, 500, etc).
                Não precisa de configuração adicional.
              </p>

              <div className="bg-muted/50 rounded-lg p-3">
                <p className="text-sm text-muted-foreground">
                  <strong>Exemplos de erros detectados:</strong>
                </p>
                <ul className="text-sm space-y-1 text-muted-foreground mt-2">
                  <li>• <code className="bg-muted px-1 rounded">400</code> - Requisição inválida</li>
                  <li>• <code className="bg-muted px-1 rounded">404</code> - Não encontrado</li>
                  <li>• <code className="bg-muted px-1 rounded">500</code> - Erro interno do servidor</li>
                </ul>
              </div>
            </div>

            <Separator />

            {/* Exemplo prático W2SAT */}
            <div className="space-y-3">
              <h3 className="font-semibold flex items-center gap-2">
                <Lightbulb className="h-4 w-4 text-amber-500" />
                Exemplo Prático: Consulta de Boletos por CPF
              </h3>
              
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 space-y-3">
                <p className="text-sm">
                  <strong>Cenário:</strong> Usuário digita CPF para consultar boletos. 
                  Se não houver boletos, a API retorna array vazio <code className="bg-muted px-1 rounded">[]</code>.
                </p>
                
                <div className="text-sm space-y-2">
                  <p className="font-medium">Configuração recomendada:</p>
                  <ul className="space-y-1 text-muted-foreground">
                    <li>• <strong>Tipo:</strong> Array/campo vazio</li>
                    <li>• <strong>Caminho:</strong> <em>(deixar vazio)</em></li>
                    <li>• <strong>Ação:</strong> Enviar mensagem e repetir</li>
                    <li>• <strong>Mensagem:</strong> "Não encontramos boletos para este CPF. Verifique e digite novamente."</li>
                    <li>• <strong>Tentativas máximas:</strong> 3</li>
                  </ul>
                </div>

                <div className="text-sm space-y-1">
                  <p className="font-medium">Fluxo:</p>
                  <ol className="list-decimal list-inside text-muted-foreground space-y-1">
                    <li>Usuário digita CPF inválido</li>
                    <li>API retorna <code className="bg-muted px-1 rounded">[]</code> (array vazio)</li>
                    <li>Sistema detecta erro e envia mensagem</li>
                    <li>Volta para o node de pergunta do CPF</li>
                    <li>Usuário tenta novamente (até 3x)</li>
                  </ol>
                </div>
              </div>
            </div>

            <Separator />

            {/* Dicas */}
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
              <h3 className="font-semibold text-sm flex items-center gap-2 mb-2">
                <Lightbulb className="h-4 w-4 text-blue-500" />
                Dicas
              </h3>
              <ul className="text-sm space-y-2 text-muted-foreground">
                <li className="flex items-start gap-2">
                  <ArrowRight className="h-4 w-4 mt-0.5 shrink-0 text-blue-500" />
                  <span>
                    <strong>Teste a API primeiro</strong> para entender a estrutura da resposta
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <ArrowRight className="h-4 w-4 mt-0.5 shrink-0 text-blue-500" />
                  <span>
                    Verifique o que a API retorna quando o dado <strong>NÃO existe</strong>
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <ArrowRight className="h-4 w-4 mt-0.5 shrink-0 text-blue-500" />
                  <span>
                    O caminho usa notação de ponto (ex: <code className="bg-muted px-1 rounded">resultado.dados.items</code>)
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <ArrowRight className="h-4 w-4 mt-0.5 shrink-0 text-blue-500" />
                  <span>
                    Para verificar a resposta inteira na raiz, <strong>deixe o caminho vazio</strong>
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </ScrollArea>

        <DialogFooter className="px-6 py-4 border-t bg-muted/30 shrink-0">
          <Button onClick={() => onOpenChange(false)}>
            Entendi
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
